import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type AnyRoute,
  type RootRoute,
} from '@tanstack/react-router'
import { EmptyState } from '@/components/ui'
import { moduleRegistry } from './registry'
import { canAccessModule, canAccessRoute, type AuthUser } from './permissions'
import type { ValidatedModule } from './types'

/**
 * Builds a TanStack Router from the manifest registry. The shell supplies
 * the root element (Sidebar + Topbar + <Outlet/>) and an auth-user
 * accessor so `beforeLoad` hooks can reject unauthorized navigation.
 */
export function buildRouter(options: {
  rootComponent: React.ComponentType
  /** Returns the current auth user — called inside `beforeLoad` so it
   *  always reads the freshest identity. */
  getAuth: () => AuthUser
  /** Optional: override the router's history (tests use MemoryHistory). */
  history?: ReturnType<typeof createMemoryHistory>
}) {
  const rootRoute: RootRoute = createRootRoute({
    // TanStack's RouteComponent type narrows to FunctionComponent. Our
    // ModuleConfig.routes accept ComponentType<{}> for ergonomics, so we
    // cast at the boundary — runtime contract is unchanged.
    component: options.rootComponent as unknown as React.FC,
    notFoundComponent: NotFound,
  })

  // Skip the HomeStub fallback when a module claims `/` for itself —
  // e.g. the M10 Dashboard module. If no module owns `/`, keep the
  // "pick a module from the sidebar" stub so fresh installs don't 404.
  const dashboardModuleClaims = moduleRegistry.some(
    (m) => m.resolvedNamespace === '/',
  )

  const children: AnyRoute[] = [
    ...(dashboardModuleClaims
      ? []
      : [
          createRoute({
            getParentRoute: () => rootRoute,
            path: '/',
            component: HomeStub,
          }),
        ]),
    ...moduleRegistry.flatMap((module) => moduleRoutes(rootRoute, module, options.getAuth)),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/_403',
      component: Forbidden,
    }),
  ]

  const routeTree = rootRoute.addChildren(children)

  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    history: options.history,
  })
}

function moduleRoutes(
  rootRoute: RootRoute,
  module: ValidatedModule,
  getAuth: () => AuthUser,
): AnyRoute[] {
  // Modules that claim `/` (e.g. M10 Dashboard) collapse to a single
  // leaf route — we can't have a parent + child both at `/`. All other
  // modules use the parent/child pattern so they can grow sub-routes.
  const claimsRoot = module.resolvedNamespace === '/'
  const singleRoute = claimsRoot && module.routes.length === 1 && module.routes[0]!.path === '/'
    ? module.routes[0]!
    : null

  if (singleRoute) {
    return [
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/',
        beforeLoad: () => {
          const user = getAuth()
          if (!canAccessModule(user, module) || !canAccessRoute(user, module, singleRoute)) {
            throw redirect({ to: '/_403' })
          }
        },
        component: singleRoute.element as unknown as React.FC,
      }),
    ]
  }

  const moduleRoot = createRoute({
    getParentRoute: () => rootRoute,
    path: module.resolvedNamespace,
    beforeLoad: () => {
      const user = getAuth()
      if (!canAccessModule(user, module)) {
        throw redirect({ to: '/_403' })
      }
    },
    component: ModuleShell,
  })

  const routes = module.routes.map((route) => {
    // TanStack prefers `/` as an index route; convert per-manifest `/` to
    // the moduleRoot's index.
    const path = route.path === '/' ? '/' : route.path
    return createRoute({
      getParentRoute: () => moduleRoot,
      path,
      beforeLoad: () => {
        const user = getAuth()
        if (!canAccessRoute(user, module, route)) {
          throw redirect({ to: '/_403' })
        }
      },
      component: route.element as unknown as React.FC,
    })
  })

  return [moduleRoot.addChildren(routes)]
}

function ModuleShell() {
  return <Outlet />
}

function HomeStub() {
  return (
    <div className="mx-auto max-w-lg p-10">
      <EmptyState
        variant="no-data"
        title="Pick a module from the sidebar"
        description="Press Cmd+K to search, or use the leader-key shortcuts listed in each sidebar item's tooltip."
      />
    </div>
  )
}

function Forbidden() {
  return (
    <div className="mx-auto max-w-lg p-10">
      <EmptyState
        variant="error"
        title="403 — access denied"
        description="Your role doesn't include permission for this module. Ask an admin to grant access in Settings."
      />
    </div>
  )
}

function NotFound() {
  return (
    <div className="mx-auto max-w-lg p-10">
      <EmptyState
        variant="no-match"
        title="404 — not found"
        description="The path you opened doesn't map to any module."
      />
    </div>
  )
}

/** Registry declaration for typed Link components. */
declare module '@tanstack/react-router' {
  interface Register {
    // Concrete router type is runtime-derived; we keep this weak so
    // consumers fall back to string paths without type-check pain.
    router: ReturnType<typeof buildRouter>
  }
}
